// YERİ: src/app/domains/site/sections/listing-detail.ts
//
// İlan ayrıntı penceresi (2026-10-11) — emlak ilanı ve satılık/kiralık araç kartına
// tıklayınca açılır: büyük fotoğraf + küçük resimler, bilgiler, açıklamanın tamamı ve
// "Bilgi iste" formu. Form işletmenin Websitem → Formlar kutusuna düşer (konu satırında
// ilanın adı), yani ziyaretçi ilanla ilgili soruyu siteden ayrılmadan sorar.
// "Bildir" bağlantısı burada da var (işletme kapattıysa kendiliğinden gizlenir).

import { Component, HostListener, computed, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SiteChromeText } from '../core/site-chrome-text';
import { TenantWebService } from '../core/tenant-web.service';
import { ContentReportBody } from '../core/tenant-web.service';
import { SiteReport } from '../site-report';

export interface ListingDetailData {
  id: number;
  title: string;
  badge?: string | null;
  priceText?: string | null;
  facts: string[];
  description?: string | null;
  images: string[];
}

@Component({
  selector: 'site-listing-detail',
  standalone: true,
  imports: [FormsModule, SiteReport],
  template: `
    <div class="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4 sm:p-8" (click)="closed.emit()">
      <div class="relative w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-neutral-900" role="dialog" aria-modal="true" (click)="$event.stopPropagation()">
        <button type="button" class="absolute right-3 top-3 z-10 rounded-full bg-black/50 px-3 py-1 text-sm text-white hover:bg-black/70" (click)="closed.emit()">{{ text.t('pf.close') }}</button>

        @if (current(); as img) {
          <img [src]="img" [alt]="data().title" class="aspect-[16/10] w-full bg-neutral-100 object-cover dark:bg-neutral-800" />
        }
        @if (data().images.length > 1) {
          <div class="flex gap-2 overflow-x-auto px-6 pt-3">
            @for (g of data().images; track g; let i = $index) {
              <button type="button" class="shrink-0 overflow-hidden rounded-md border-2"
                      [style.border-color]="i === index() ? 'var(--site-primary)' : 'transparent'" (click)="index.set(i)">
                <img [src]="g" alt="" loading="lazy" class="h-14 w-20 object-cover" />
              </button>
            }
          </div>
        }

        <div class="p-6">
          @if (data().badge) {
            <span class="rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-white" style="background:var(--site-primary)">{{ data().badge }}</span>
          }
          <h3 class="mt-2 text-2xl font-bold text-neutral-900 dark:text-white">{{ data().title }}</h3>
          @if (data().priceText) {
            <p class="mt-2 text-2xl font-bold tabular-nums" style="color:var(--site-primary)">{{ data().priceText }}</p>
          }
          @if (data().facts.length) {
            <div class="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-neutral-600 dark:text-neutral-400">
              @for (f of data().facts; track f) { <span>{{ f }}</span> }
            </div>
          }
          @if (data().description) {
            <p class="mt-4 whitespace-pre-line leading-relaxed text-neutral-700 dark:text-neutral-300">{{ data().description }}</p>
          }

          <!-- Bilgi iste → işletmenin Formlar kutusu -->
          <div class="mt-6 rounded-xl border border-neutral-200 p-4 dark:border-neutral-700">
            <p class="font-semibold text-neutral-900 dark:text-white">{{ text.t('ls.inquire') }}</p>
            @if (sent()) {
              <p class="mt-2 text-sm text-emerald-700 dark:text-emerald-400">{{ text.t('ls.sent') }}</p>
            } @else {
              <div class="mt-3 grid gap-3 sm:grid-cols-2">
                <input class="rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-950" [placeholder]="text.t('ls.name')" [(ngModel)]="name" maxlength="120" />
                <input class="rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-950" type="email" [placeholder]="text.t('ls.email')" [(ngModel)]="email" maxlength="200" />
                <input class="rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-950 sm:col-span-2" type="tel" [placeholder]="text.t('ls.phone')" [(ngModel)]="phone" maxlength="40" />
                <textarea class="rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-950 sm:col-span-2" rows="3" [placeholder]="text.t('ls.message')" [(ngModel)]="message" maxlength="2000"></textarea>
              </div>
              @if (error()) { <p class="mt-2 text-sm text-rose-600">{{ error() }}</p> }
              <button type="button" class="mt-3 rounded-full px-5 py-2 text-sm font-semibold text-white disabled:opacity-60" style="background:var(--site-primary)"
                      [disabled]="sending()" (click)="send()">{{ text.t('ls.send') }}</button>
            }
          </div>

          <div class="mt-4 text-right"><site-report [clientId]="clientId()" [targetType]="targetType()" [targetId]="data().id" [targetTitle]="data().title" /></div>
        </div>
      </div>
    </div>
  `,
})
export class ListingDetail {
  protected text = inject(SiteChromeText);
  private svc = inject(TenantWebService);

  data = input.required<ListingDetailData>();
  clientId = input<number>(0);
  targetType = input.required<ContentReportBody['targetType']>();
  closed = output<void>();

  protected index = signal(0);
  protected current = computed(() => this.data().images[this.index()] ?? null);

  protected name = '';
  protected email = '';
  protected phone = '';
  protected message = '';
  protected sending = signal(false);
  protected sent = signal(false);
  protected error = signal<string | null>(null);

  @HostListener('document:keydown.escape')
  protected onEsc(): void { this.closed.emit(); }

  protected send(): void {
    if (!this.email.trim() && !this.phone.trim()) { this.error.set(this.text.t('ls.needContact')); return; }
    this.error.set(null);
    this.sending.set(true);
    this.svc.postForm(this.clientId(), {
      name: this.name.trim() || undefined,
      email: this.email.trim() || undefined,
      phone: this.phone.trim() || undefined,
      subject: `${this.text.t('ls.subject')}: ${this.data().title}`.slice(0, 200),
      message: this.message.trim() || undefined,
    }).subscribe({
      next: ok => { this.sending.set(false); ok ? this.sent.set(true) : this.error.set(this.text.t('ls.failed')); },
      error: () => { this.sending.set(false); this.error.set(this.text.t('ls.failed')); },
    });
  }
}
